import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import logo from "../assets/brand/logo.svg";
import logoMobile from "../assets/brand/logomobile.svg";
import globe from "../assets/brand/globe.svg";
import menu from "../assets/brand/menu.svg";
import close from "../assets/brand/close.svg";
import LanguageToggle from "./common/LanguageToggle";
import Button from "./ui/Button";
import IconButton from "./ui/IconButton";

const NAV_ITEMS = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Products", to: "/products" },
  { label: "Careers", to: "/careers" },
  // { label: 'Volunteer', to: '/volunteers' },
  { label: "Blog", to: "/blog" },
  { label: "Contact Us", to: "/contact" },
] as const;

const LOCALE_LABEL = "English (UK)";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-sm border-b border-gray-50 flex justify-center">
      <div className="layout-container w-full max-w-[1700px] flex min-h-[92px] items-center justify-between gap-6 max-[1100px]:min-h-[78px] max-[768px]:min-h-[72px]">
        {/* Left Side: Logo */}
        <Link
          to="/"
          className="inline-flex flex-shrink-0 items-center"
          aria-label="Bao Technologies homepage"
        >
          <img
            src={logo}
            alt="Bao Technologies"
            className="block h-auto w-[200px] max-[768px]:hidden"
          />
          <img
            src={logoMobile}
            alt="Bao Technologies"
            className="hidden h-auto w-[36px] max-[768px]:block"
          />
        </Link>

        {/* Center: Navigation Links */}
        <nav
          className="flex flex-1 items-center justify-center gap-[32px] max-[1100px]:hidden"
          aria-label="Primary"
        >
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }: { isActive: boolean }) =>
                [
                  "whitespace-nowrap font-title text-[16px] font-[300] leading-[1.2] transition-colors hover:text-primary",
                  isActive ? "text-primary" : "text-[#919191]",
                ].join(" ")
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Right Side: Actions */}
        <div className="ml-auto inline-flex flex-shrink-0 items-center justify-end gap-[32px] max-[1100px]:gap-[14px] max-[768px]:gap-[12px]">
          <div className="flex items-center gap-[19px]">
            <LanguageToggle
              className={[
                "inline-flex items-center gap-[8px] border-0 bg-transparent p-0 font-title text-[16px] font-[300] text-[#9C9C9C] cursor-pointer hover:text-primary transition-colors",
                "max-[1100px]:text-[16px]",
                "max-[768px]:hidden",
              ].join(" ")}
              aria-label="Select language"
              globeSrc={globe}
              fullLabel={LOCALE_LABEL}
              shortLabel="EN"
              globeClassName="h-[24px] w-[24px] opacity-60"
              fullLabelClassName="max-[1100px]:hidden"
              shortLabelClassName="hidden max-[1100px]:inline"
              chevronClassName="h-[16px] w-[16px]"
            />
          </div>

          <Link to="/contact">
            <Button
              className={[
                "rounded-[12px] border-0 bg-[#2776EA] w-[180px] h-[48px] flex items-center justify-center font-title text-[16px] font-[400] leading-none text-white cursor-pointer",
                "hover:bg-blue-600 transition-all shadow-md",
                "max-[768px]:w-auto max-[768px]:px-[22px] max-[768px]:h-[40px]",
              ].join(" ")}
            >
              Get in touch
            </Button>
          </Link>

          <IconButton
            className="hidden items-center justify-center border-0 bg-transparent p-0 cursor-pointer max-[1100px]:inline-flex"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            iconSrc={menuOpen ? close : menu}
            iconClassName="block h-[24px] w-[24px]"
          />
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`absolute left-0 right-0 top-full hidden w-full bg-white border-b border-gray-50 shadow-lg transition-opacity duration-200 max-[1100px]:block ${menuOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        aria-hidden={!menuOpen}
      >
        <nav
          className="layout-container w-full max-w-[1700px] px-5 py-4 sm:px-8 xl:px-0 flex flex-col"
          aria-label="Mobile"
        >
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }: { isActive: boolean }) =>
                [
                  "py-3 font-title text-[16px] font-[300] leading-[1.2] transition-colors hover:text-primary",
                  isActive ? "text-primary" : "text-[#919191]",
                ].join(" ")
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            onClick={() => setMenuOpen(false)}
            className="pt-3 pb-2"
          >
            <Button className="w-full rounded-[12px] border-0 bg-[#2776EA] h-[48px] flex items-center justify-center font-title text-[16px] font-[400] leading-none text-white cursor-pointer hover:bg-blue-600 transition-all shadow-md">
              Get in touch
            </Button>
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Header;
